import { Logo } from './Logo';

export interface LogoLoadingProps {
  size?: number;
  label?: string;
}

/**
 * The loading state — the Railit mark, with the track running under you.
 *
 * WHAT THIS REPLACED: two polygons of the old Join Future glyph, ported from
 * `erp-app/public/loading.html` and hard-coded to `var(--ember)` and
 * `var(--cream)`. It survived the rebrand because nothing about it referenced
 * the logo, so every page in the product flashed the previous company's mark
 * while it loaded, in colours the brand no longer uses.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE TIMING IS OVERRIDDEN HERE AND NOT IN Logo
 *
 * Logo's own animation is built for a MARK that happens to move: the sleepers
 * fade between 45% and 75% of a 1.6s cycle, which means that for more than a
 * second of every cycle the artwork is perfectly still. Beside a line of chat
 * text that reads as a restrained flicker. At 96px in the middle of an empty
 * page, waiting for something, it reads as a picture that is not loading.
 *
 * A loader has the opposite job from a logo: it must never look stopped, or the
 * page looks hung. So the wave is spread across the WHOLE cycle — four sleepers
 * a quarter-cycle apart — and each one goes fully out and back. At every instant
 * one of them is moving, and the eye follows it up the track.
 *
 * The override is scoped to this component. The header and the chat keep the
 * quieter brand animation, because a logo that behaves like a spinner is a logo
 * that has stopped being a logo.
 *
 * `prefers-reduced-motion` still stops everything: Logo's own rule switches the
 * sleepers off, and the rule below is switched off with it — a person who has
 * asked the system for stillness gets a still mark and the label to read.
 */
export function LogoLoading({ size = 96, label }: LogoLoadingProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading'}
      className="rail-loading inline-flex items-center justify-center text-foreground/70"
      style={{ width: size, height: size }}
    >
      <style>{`
        @keyframes rail-loading-run {
          0%      { opacity: 1 }
          35%     { opacity: 0.06 }
          70%,100%{ opacity: 1 }
        }
        .rail-loading .rail-sleeper {
          animation: rail-loading-run 1.4s ease-in-out infinite;
        }
        /* A quarter of the cycle between each, so the gap travels the track
           continuously instead of all four blinking and then waiting. */
        .rail-loading .rail-sleeper-0 { animation-delay: 0s }
        .rail-loading .rail-sleeper-1 { animation-delay: .35s }
        .rail-loading .rail-sleeper-2 { animation-delay: .7s }
        .rail-loading .rail-sleeper-3 { animation-delay: 1.05s }
        @media (prefers-reduced-motion: reduce) {
          .rail-loading .rail-sleeper { animation: none }
        }
      `}</style>
      <Logo size={size} markOnly animate />
    </div>
  );
}
